/**
 * Logo component mit React.memo.
 * @module components/theme/Logo/Logo
 */
import React, { memo, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { getNavroot } from '@plone/volto/actions/navroot/navroot';
import { flattenToAppURL, getBaseUrl } from '@plone/volto/helpers/Url/Url';
import { hasApiExpander } from '@plone/volto/helpers/Utils/Utils';
import Image from '@plone/volto/components/theme/Image/Image';

// Importieren der eigenen Logo-Dateien
import LogoImage from './logo_hrl_res.png';
import LogoImageUni from './logo_uni_short_res.png';
import './Logo.css';

// Memo-isierte Bild-Komponente
const MemoizedImage = memo(({ src, alt, className, style }) => {
  return <Image className={className} style={style} src={src} alt={alt} />;
});

/**
 * Logo component class.
 * @function Logo
 * @param {Object} intl Intl object
 * @returns {string} Markup of the component.
 */
const Logo = () => {
  const pathname = useLocation().pathname;
  const site = useSelector((state) => state.site.data);
  const navroot = useSelector((state) => state.navroot.data);
  const dispatch = useDispatch();
  const intl = useIntl();

  const messages = defineMessages({
    home: {
      id: 'Home',
      defaultMessage: 'Home',
    },
    logoOf: {
      id: 'Logo of',
      defaultMessage: 'Logo of',
    },
  });

  useEffect(() => {
    if (pathname && !hasApiExpander('navroot', getBaseUrl(pathname))) {
      dispatch(getNavroot(getBaseUrl(pathname)));
    }
  }, [dispatch, pathname]);

  const navRootPath = flattenToAppURL(navroot?.navroot?.['@id']) || '/';

  // Externe URL für das Uni-Logo (ersetzen Sie dies durch Ihre tatsächliche URL)
  const externalUniURL = 'https://www.uni-bonn.de';

  // Inline-Stil als Fallback oder zusätzliche Kontrolle
  const logoImageStyle = {
    height: '110px',
    width: 'auto',
    objectFit: 'contain',
  };

  return (
    <div className="logo-container">
      {/* Uni-Logo mit externem Link */}
      <a
        href={externalUniURL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`${intl.formatMessage(messages.logoOf)} Universität`}
        className="logo-uni"
      >
        <MemoizedImage
          className="ui image logo-image"
          style={logoImageStyle}
          src={LogoImageUni}
          alt={`${intl.formatMessage(messages.logoOf)} Universität`}
        />
      </a>

      {/* HRL-Logo mit Link zum lokalen Home */}
      <Link to={navRootPath} aria-label={intl.formatMessage(messages.home)}>
        <MemoizedImage
          className="ui image logo-image"
          style={logoImageStyle}
          src={LogoImage}
          alt={
            intl.formatMessage(messages.logoOf) + ' ' + site['plone.site_title']
          }
        />
      </Link>
    </div>
  );
};

// Memo-isierung der gesamten Logo-Komponente
export default memo(Logo);
